import React, { useRef, useEffect, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import './Chart.scss';
import { prefixClass } from 'lib/utils';
import { ReportViewModel } from 'types/report';
import ChartLegend from './components/ChartLegend';
// Using requestAnimationFrame to throttle dimension updates for smoother resizing
import { ChartContextProvider, useChartContext } from './context/ChartContext';

import { ChartDataColumn, ChartGroup, ChartProps as ChartPropsType, ChartFilters, ChartDimensions } from './types';
import { chartContextDefault } from './components/ChartBlock/chartCFG';
import ChartBlock from './components/ChartBlock';
import classNames from 'classnames';
import ChartNavigation from './components/ChartNavigation';

type ChartComponentProps = {
  rawData: ReportViewModel | null;
  height: number;
  initialFilters?: {
    actuals: boolean;
    budget: boolean;
    variance: boolean;
    money: boolean;
    percentage: boolean;
  };
  selectedRow?: string | null;
  selectedColumn?: number | string | null;
  reversed?: boolean;
  onClose?: () => void;
  onSelectionChange?: (rowId: string) => void;
  onColumnSelectionChange?: (columnId: number | string | null) => void;
};

type ChartContentProps = Record<string, never>;

const ChartContent = (_props: ChartContentProps) => {
  // Use the Chart context
  const {
    chartData,
    dimensions,
    selectedRow,
    reversed,
    chartFilters,
    rawData,
    height,
    setDimensions,
    selectedColumn,
    handleColumnSelectionChange,
  } = useChartContext();

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Constants
  const marginHorizontal = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--ks-chart-margin-horizontal')
  );
  const marginVertical = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--ks-chart-margin-vertical')
  );

  const prefix = prefixClass('chart');

  // Compute dimensions synchronously to avoid an extra frame of lag during width resize
  const updateDimensions = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const containerWidth = Math.round(rect.width);
    // Measure header height dynamically
    const headerHeight = headerRef.current ? Math.round(headerRef.current.getBoundingClientRect().height) : 0;
    // Dynamically reserve X-axis space depending on whether labels need rotation
    // Estimate if labels will rotate using the same condition as drawXLabels
    let xAxisHeight = 80; // default/fallback
    try {
      const columns = chartData?.columns || [];
      const colCount = columns.length;
      if (colCount > 0) {
        const groupsCount = chartFilters?.groups?.length || 0;
        const groupGap = groupsCount > 1 ? (colCount > 8 ? 8 : 12) : 4;
        // Predict the inner chart width used to compute groupWidth
        const predictedCanvasWidth =
          containerWidth < 600 ? containerWidth - marginHorizontal : containerWidth - marginHorizontal * 2;
        const innerMargin = chartContextDefault.body.margin;
        const chartWidthInside = Math.max(0, predictedCanvasWidth - innerMargin.left - innerMargin.right);
        const groupWidth = colCount > 0 ? (chartWidthInside - (colCount - 1) * groupGap) / colCount : 0;

        // Prepare label text similarly to drawXLabels
        const firstColName = columns[0]?.name || '';
        const isLocation = !!chartData?.isLocation;
        const consolidated = !!chartData?.consolidated;
        const splitter = isLocation && !consolidated ? ' - ' : ', ';
        const labelToCheck = firstColName.split(splitter)[0] || firstColName;
        // Measure label width with matching font
        const font = chartContextDefault.body.font;
        const fontSize = chartContextDefault.body.fontSize.label;
        const tmpCanvas = document.createElement('canvas');
        const mctx = tmpCanvas.getContext('2d');
        let labelWidth = 0;
        if (mctx) {
          mctx.font = `${fontSize}px ${font}`;
          labelWidth = mctx.measureText(labelToCheck).width;
        }
        const shouldRotateLabels = labelWidth > Math.max(0, groupWidth - groupGap);
        // Use smaller reserved height if no rotation needed
        xAxisHeight = shouldRotateLabels ? 80 : 40;
      }
    } catch {
      // Keep fallback xAxisHeight on any errors
    }

    const rawHeight =
      containerWidth < 600
        ? height - marginVertical - xAxisHeight - headerHeight
        : height - marginVertical * 2 - xAxisHeight - headerHeight;
    const rawWidth = containerWidth < 600 ? containerWidth - marginHorizontal : containerWidth - marginHorizontal * 2;
    // Round to integers to avoid sub-pixel thrashing and clamp to >= 0
    const next = {
      width: Math.max(0, Math.round(rawWidth)),
      height: Math.max(0, Math.round(rawHeight)),
    };
    // Skip state update if nothing actually changed
    if (!dimensions || next.width !== dimensions.width || next.height !== dimensions.height) {
      setDimensions(next);
    }
  }, [height, marginHorizontal, marginVertical, dimensions, setDimensions, chartData, chartFilters]);

  // Handling the initialization of the scrollbar and set the observer so we can adapt to changes in the size of the scrollbar
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      updateDimensions();
    });

    const el = containerRef.current;
    const headerEl = headerRef.current;
    if (el) observer.observe(el);
    if (headerEl) observer.observe(headerEl);

    return () => {
      if (el) observer.unobserve(el);
      if (headerEl) observer.unobserve(headerEl);
      observer.disconnect();
    };
  }, [updateDimensions]);

  // Update dimensions when any dependency of updateDimensions changes (e.g., height/margins)
  // Use layout effect so updates apply before paint to minimize perceived lag
  useLayoutEffect(() => {
    updateDimensions();
  }, [updateDimensions]);

  const MemoizedLegend = useMemo(() => <ChartLegend data={chartData} />, [chartData]);
  const MemoizedChart = useMemo(() => {
    if (!dimensions || !chartData) return null;
    return (
      <ChartBlock
        dimensions={dimensions}
        chartData={chartData}
        chartFilters={chartFilters}
        selectedColumn={selectedColumn}
        onColumnSelectionChange={handleColumnSelectionChange}
      />
    );
  }, [dimensions, chartData, chartFilters, selectedColumn, reversed]);

  return (
    <div ref={containerRef} className={prefix()}>
      <div className={prefix('navigation-container')}>
        <ChartNavigation path={chartData?.path || null} chartData={chartData} />
      </div>
      <div ref={headerRef} className={prefix('header')}>
        {/*  <div className={prefix('timeframe')}>{chartData?.timeFrame}</div> */}
        {!chartData?.isParent && MemoizedLegend}
      </div>
      {MemoizedChart}
    </div>
  );
};

const Chart = ({
  rawData,
  height,
  initialFilters,
  selectedRow,
  selectedColumn,
  onClose,
  onSelectionChange,
  onColumnSelectionChange,
  reversed,
}: ChartComponentProps) => {
  return (
    <ChartContextProvider
      rawData={rawData}
      height={height}
      initialFilters={initialFilters}
      selectedRow={selectedRow}
      selectedColumn={selectedColumn}
      onClose={onClose}
      onSelectionChange={onSelectionChange}
      onColumnSelectionChange={onColumnSelectionChange}
      initialReversed={reversed}
    >
      <ChartContent />
    </ChartContextProvider>
  );
};

export default Chart;
